/**
 * ConfirmPH: Daily checklists page.
 *
 * Tabs:
 *   • Today       : start / continue today's runs grouped by template type
 *   • History     : list/filter past runs
 *   • Templates   : owner-only: manage checklist templates
 */
import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  ClipboardCheck, Plus, RefreshCw, ChevronRight, Loader2, AlertOctagon,
  ListChecks, CalendarDays, Settings as SettingsIcon, CheckCircle2,
  Car, FileText, Pencil, Trash2,
} from "lucide-react";

const TABS = [
  { key: "today",     label: "Today",     icon: CalendarDays },
  { key: "history",   label: "History",   icon: ListChecks },
  { key: "templates", label: "Templates", icon: SettingsIcon, ownerOnly: true },
];

const STATUS_BADGE = {
  not_started:  "bg-slate-100 text-slate-700 border-slate-200",
  in_progress:  "bg-amber-100 text-amber-800 border-amber-200",
  completed:    "bg-emerald-100 text-emerald-800 border-emerald-200",
  missed:       "bg-rose-100 text-rose-800 border-rose-200",
};

const TYPE_LABEL = {
  opening: "Opening",
  closing: "Closing",
  vehicle_readiness: "Vehicle readiness",
  cleaning: "Cleaning",
  fuel_charge: "Fuel / charge",
  handover: "Handover",
};

export default function DailyChecklists() {
  const { user } = useAuth();
  const isOwner = (user?.account_type || user?.role) === "business_owner"
                  || (user?.account_type || user?.role) === "platform_admin";
  const [tab, setTab] = useState("today");
  const [activeRunId, setActiveRunId] = useState(null);

  if (activeRunId) {
    return (
      <RunEditor
        runId={activeRunId}
        onClose={() => setActiveRunId(null)}
      />
    );
  }

  return (
    <div className="space-y-5 max-w-5xl" data-testid="daily-checklists-page">
      <header>
        <h1 className="font-display text-3xl font-bold inline-flex items-center gap-2">
          <ClipboardCheck className="w-6 h-6 text-emerald-700" /> Daily checklists
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Quick operational checks for staff and owners: opening, closing, vehicle readiness,
          cleaning, handover. Completed checklists are saved with timestamps and the staff member who finished them.
        </p>
      </header>

      <nav className="flex gap-2 border-b border-slate-200" data-testid="checklists-tabs">
        {TABS.filter(t => !t.ownerOnly || isOwner).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`checklists-tab-${key}`}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 transition-colors -mb-px ${
              tab === key
                ? "border-emerald-700 text-emerald-800 font-semibold"
                : "border-transparent text-slate-600 hover:text-slate-900"
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </nav>

      {tab === "today" && <TodayTab onOpen={setActiveRunId} />}
      {tab === "history" && <HistoryTab onOpen={setActiveRunId} />}
      {tab === "templates" && isOwner && <TemplatesTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Today tab
// ---------------------------------------------------------------------------
function TodayTab({ onOpen }) {
  const [data, setData] = useState(null);
  const [busyType, setBusyType] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [showVehiclePicker, setShowVehiclePicker] = useState(null); // template

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/checklists/today");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await load(); })();
    return () => { cancelled = true; };
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/vehicles");
        setVehicles(Array.isArray(r.data) ? r.data : (r.data.items || []));
      } catch (_) { /* ignore */ }
    })();
  }, []);

  const start = async (template, vehicleId = null) => {
    setBusyType(template.type);
    try {
      const { data } = await api.post("/checklists/runs", {
        template_id: template.id,
        vehicle_id: vehicleId,
      });
      onOpen(data.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyType(null);
    }
  };

  if (!data) {
    return (
      <div className="py-10 text-center text-slate-500">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {data.missed.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900"
             data-testid="checklists-missed-banner">
          <div className="flex items-center gap-2 font-semibold">
            <AlertOctagon className="w-4 h-4" />
            {data.missed.length} unfinished checklist{data.missed.length === 1 ? "" : "s"} from earlier days
          </div>
          <div className="mt-2 space-y-1">
            {data.missed.slice(0, 5).map(m => (
              <button key={m.id} onClick={() => onOpen(m.id)}
                      className="text-xs underline block text-left">
                {m.business_date} · {m.template_name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3" data-testid="checklists-today-groups">
        {data.groups.map((g) => (
          <TemplateCardToday
            key={g.template.id}
            group={g}
            busy={busyType === g.template.type}
            vehicles={vehicles}
            onStart={(vehicleId) => start(g.template, vehicleId)}
            onOpen={onOpen}
            showVehiclePicker={showVehiclePicker === g.template.id}
            requestVehiclePicker={() => setShowVehiclePicker(g.template.id)}
            cancelVehiclePicker={() => setShowVehiclePicker(null)}
          />
        ))}
      </div>
    </div>
  );
}

function TemplateCardToday({
  group, busy, vehicles, onStart, onOpen,
  showVehiclePicker, requestVehiclePicker, cancelVehiclePicker,
}) {
  const t = group.template;
  const runs = group.today_runs || [];
  const isVehicleType = t.type === "vehicle_readiness";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4"
         data-testid={`checklist-today-${t.type}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs text-slate-500">{TYPE_LABEL[t.type] || t.type}</div>
          <h3 className="font-display text-lg font-semibold">{t.name}</h3>
          <div className="text-xs text-slate-500 mt-0.5">{(t.items || []).length} items</div>
        </div>
        <button
          type="button"
          onClick={isVehicleType ? requestVehiclePicker : () => onStart(null)}
          disabled={busy}
          className="px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-800 inline-flex items-center gap-1.5 disabled:opacity-60"
          data-testid={`checklist-start-${t.type}`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          New
        </button>
      </div>

      {isVehicleType && showVehiclePicker && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-2"
             data-testid="checklist-vehicle-picker">
          <div className="text-xs text-slate-600 mb-1.5">Pick a vehicle:</div>
          <div className="max-h-40 overflow-auto space-y-1">
            {vehicles.map(v => (
              <button key={v.id}
                      onClick={() => { onStart(v.id); cancelVehiclePicker(); }}
                      className="w-full text-left text-xs px-2 py-1 rounded hover:bg-white border border-transparent hover:border-slate-300 inline-flex items-center gap-1.5"
                      data-testid={`checklist-pick-vehicle-${v.id}`}>
                <Car className="w-3 h-3" /> {v.name || v.plate_number}
              </button>
            ))}
            {vehicles.length === 0 && (
              <div className="text-xs text-slate-500 px-2 py-1.5">No vehicles yet.</div>
            )}
          </div>
          <button type="button" onClick={cancelVehiclePicker}
                  className="mt-1.5 text-xs text-slate-500 underline">
            Cancel
          </button>
        </div>
      )}

      {runs.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {runs.map(r => (
            <li key={r.id}>
              <button onClick={() => onOpen(r.id)}
                      className="w-full text-left text-xs px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-white flex items-center justify-between gap-2"
                      data-testid={`checklist-run-${r.id}`}>
                <span className="truncate">
                  {r.vehicle_label ? `${r.vehicle_label} · ` : ""}
                  Started {r.started_at?.slice(11, 16)}
                </span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_BADGE[r.status] || ""}`}>
                  {r.status.replace("_", " ")}
                </span>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Run editor
// ---------------------------------------------------------------------------
function RunEditor({ runId, onClose }) {
  const [run, setRun] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/checklists/runs/${runId}`);
      setRun(data);
      setNotes(data.notes || "");
    } catch (e) {
      toast.error(formatApiError(e));
      onClose();
    }
  }, [runId, onClose]);

  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await load(); })();
    return () => { cancelled = true; };
  }, [load]);

  const update = async (itemId, patch) => {
    try {
      const { data } = await api.post(
        `/checklists/runs/${runId}/items/${itemId}`, patch);
      setRun(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/checklists/runs/${runId}/submit`, { notes });
      toast.success("Checklist submitted");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!run) {
    return (
      <div className="py-10 text-center text-slate-500">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const completed = run.status === "completed";

  return (
    <div className="max-w-3xl space-y-5" data-testid="checklist-run-editor">
      <button onClick={onClose}
              className="text-sm text-slate-600 hover:text-slate-900 underline">
        ← Back to checklists
      </button>
      <header>
        <h1 className="font-display text-2xl font-bold inline-flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-emerald-700" /> {run.template_name}
        </h1>
        <div className="mt-1 text-xs text-slate-600 flex items-center gap-2 flex-wrap">
          <span>{TYPE_LABEL[run.type] || run.type}</span>
          <span>·</span>
          <span>{run.business_date}</span>
          {run.vehicle_label && (<>
            <span>·</span><span><Car className="w-3 h-3 inline mr-0.5" /> {run.vehicle_label}</span>
          </>)}
          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_BADGE[run.status] || ""}`}>
            {run.status.replace("_", " ")}
          </span>
        </div>
      </header>

      <ul className="space-y-2" data-testid="checklist-items">
        {(run.items || []).map(item => (
          <ChecklistItemRow key={item.id}
                            item={item}
                            disabled={completed}
                            onChange={(patch) => update(item.id, patch)} />
        ))}
      </ul>

      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500"
               htmlFor="run-notes">
          Notes (optional)
        </label>
        <textarea
          id="run-notes"
          disabled={completed}
          rows={3}
          maxLength={1000}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:opacity-60"
          data-testid="checklist-run-notes"
        />
      </div>

      {!completed ? (
        <button onClick={submit} disabled={submitting}
                className="px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="checklist-submit-btn">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          <CheckCircle2 className="w-4 h-4" /> Submit checklist
        </button>
      ) : (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"
             data-testid="checklist-completed-banner">
          Completed {run.completed_at?.slice(0, 16).replace("T", " ")}.
        </div>
      )}
    </div>
  );
}

function ChecklistItemRow({ item, disabled, onChange }) {
  const [showNote, setShowNote] = useState(Boolean(item.note));
  return (
    <li className={`rounded-xl border bg-white p-3 ${item.checked ? "border-emerald-200" : "border-slate-200"}`}
        data-testid={`checklist-item-${item.id}`}>
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          className="mt-1"
          disabled={disabled}
          checked={Boolean(item.checked)}
          onChange={(e) => onChange({ checked: e.target.checked })}
          data-testid={`checklist-item-check-${item.id}`}
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">
            {item.label}
            {item.required && <span className="ml-1 text-rose-600">*</span>}
          </div>
          {item.note && !showNote && (
            <div className="text-xs text-slate-600 mt-0.5 italic">Note: {item.note}</div>
          )}
        </div>
        {item.notes_allowed && !disabled && (
          <button type="button"
                  onClick={(e) => { e.preventDefault(); setShowNote(!showNote); }}
                  className="text-[11px] text-slate-500 hover:text-slate-700 underline">
            {showNote ? "Hide note" : "Add note"}
          </button>
        )}
      </label>
      {showNote && item.notes_allowed && (
        <textarea
          rows={2}
          disabled={disabled}
          maxLength={600}
          defaultValue={item.note || ""}
          onBlur={(e) => onChange({ note: e.target.value })}
          className="mt-2 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs disabled:opacity-60"
          placeholder="Notes / issues / observations"
          data-testid={`checklist-item-note-${item.id}`}
        />
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// History tab
// ---------------------------------------------------------------------------
function HistoryTab({ onOpen }) {
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", type: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.type) params.type = filters.type;
      const { data } = await api.get("/checklists/runs", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="checklist-history-tab">
      <div className="flex flex-wrap gap-2">
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                className="text-xs rounded-lg border border-slate-300 px-2 py-1.5"
                data-testid="history-filter-status">
          <option value="">All statuses</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
          <option value="missed">Missed</option>
        </select>
        <select value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
                className="text-xs rounded-lg border border-slate-300 px-2 py-1.5"
                data-testid="history-filter-type">
          <option value="">All types</option>
          {Object.keys(TYPE_LABEL).map(t => (
            <option key={t} value={t}>{TYPE_LABEL[t]}</option>
          ))}
        </select>
        <button onClick={load} className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-300 inline-flex items-center gap-1.5"
                data-testid="history-refresh-btn">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-500">
          <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…
        </div>
      ) : data.items.length === 0 ? (
        <div className="text-sm text-slate-500 italic py-6 text-center"
             data-testid="history-empty">
          No checklist runs match these filters yet.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="history-runs">
          {data.items.map(r => (
            <li key={r.id}>
              <button onClick={() => onOpen(r.id)}
                      className="w-full text-left rounded-xl border border-slate-200 bg-white px-3 py-2.5 hover:border-slate-300 flex items-center justify-between gap-2"
                      data-testid={`history-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{r.template_name}</div>
                  <div className="text-[11px] text-slate-500">
                    {r.business_date} · {TYPE_LABEL[r.type] || r.type}
                    {r.vehicle_label && ` · ${r.vehicle_label}`}
                  </div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_BADGE[r.status] || ""}`}>
                  {r.status.replace("_", " ")}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Templates tab (owner-only)
// ---------------------------------------------------------------------------
function TemplatesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/checklists/templates");
      setItems(data.items);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this template? Runs already in progress are not affected.")) return;
    try {
      await api.delete(`/checklists/templates/${id}`);
      toast.success("Template deactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (editing) {
    return <TemplateEditor template={editing} onClose={() => { setEditing(null); load(); }} />;
  }

  if (loading) {
    return (
      <div className="py-10 text-center text-slate-500">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="templates-tab">
      <p className="text-xs text-slate-500">
        Default templates are pre-seeded: you can edit names and items per company.
        Deactivated templates stay in history but no new runs can be started from them.
      </p>
      <ul className="space-y-2">
        {items.map(t => (
          <li key={t.id}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 flex items-center justify-between gap-2"
              data-testid={`template-row-${t.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-semibold flex items-center gap-2">
                {t.name}
                {!t.active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">Inactive</span>}
                {t.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">Default</span>}
              </div>
              <div className="text-[11px] text-slate-500">
                {TYPE_LABEL[t.type] || t.type} · {(t.items || []).length} items
              </div>
            </div>
            <div className="flex gap-1.5">
              <button onClick={() => setEditing(t)}
                      className="text-xs px-2 py-1 rounded-lg border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1"
                      data-testid={`template-edit-${t.id}`}>
                <Pencil className="w-3 h-3" /> Edit
              </button>
              {t.active && (
                <button onClick={() => deactivate(t.id)}
                        className="text-xs px-2 py-1 rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1"
                        data-testid={`template-deactivate-${t.id}`}>
                  <Trash2 className="w-3 h-3" /> Deactivate
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TemplateEditor({ template, onClose }) {
  const [name, setName] = useState(template.name);
  const [items, setItems] = useState(template.items || []);
  const [saving, setSaving] = useState(false);

  const addItem = () => setItems([...items, {
    id: `tmp-${Date.now()}`, label: "New item", required: true,
    notes_allowed: true, photo_required: false, order: items.length + 1,
  }]);
  const updItem = (i, patch) => setItems(items.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const delItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/checklists/templates/${template.id}`, { name, items });
      toast.success("Template saved");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="template-editor">
      <button onClick={onClose}
              className="text-sm text-slate-600 hover:text-slate-900 underline">
        ← Back to templates
      </button>
      <header>
        <h2 className="font-display text-2xl font-bold inline-flex items-center gap-2">
          <FileText className="w-5 h-5" /> Edit template
        </h2>
        <div className="text-xs text-slate-500 mt-1">{TYPE_LABEL[template.type] || template.type}</div>
      </header>

      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)}
               className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
               data-testid="template-name-input" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Items</label>
          <button onClick={addItem}
                  className="text-xs px-2 py-1 rounded-lg border border-slate-300 inline-flex items-center gap-1"
                  data-testid="template-add-item">
            <Plus className="w-3 h-3" /> Add item
          </button>
        </div>
        <ul className="space-y-2">
          {items.map((it, i) => (
            <li key={it.id || i} className="rounded-lg border border-slate-200 bg-white p-2.5"
                data-testid={`template-item-${i}`}>
              <div className="flex items-start gap-2">
                <input value={it.label} onChange={(e) => updItem(i, { label: e.target.value })}
                       className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm" />
                <button onClick={() => delItem(i)}
                        className="text-rose-600 hover:text-rose-800 p-1"
                        title="Delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="mt-2 flex items-center gap-3 text-xs text-slate-600">
                <label className="inline-flex items-center gap-1">
                  <input type="checkbox" checked={Boolean(it.required)}
                         onChange={e => updItem(i, { required: e.target.checked })} />
                  Required
                </label>
                <label className="inline-flex items-center gap-1">
                  <input type="checkbox" checked={Boolean(it.notes_allowed)}
                         onChange={e => updItem(i, { notes_allowed: e.target.checked })} />
                  Notes allowed
                </label>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <button onClick={save} disabled={saving}
              className="px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="template-save-btn">
        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
        Save template
      </button>
    </div>
  );
}
